import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, ShieldCheck, Wifi, Coffee, Phone } from "lucide-react";
import { canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/seasonal-rates-malak-bnb-resort")({
  head: () => ({
    meta: [
      { title: "Seasonal Room Rates - Malak BnB Resort" },
      { name: "description", content: "Room rates at Malak BnB Resort in Crystal Beach, Ontario vary by season. See from-pricing for all four rooms and how direct booking works." },
      { property: "og:title", content: "Seasonal Room Rates - Malak BnB Resort" },
      { property: "og:description", content: "From-pricing for every room at Malak BnB Resort, and how seasonal rates and direct booking work." },
      { property: "og:url", content: "https://malakbnbresort.ca/seasonal-rates-malak-bnb-resort" },
      { property: "og:image", content: "/images/room-comfort.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
    ],
    links: [canonical("/seasonal-rates-malak-bnb-resort")],
  }),
  component: SeasonalRatesPage,
});

const ROOMS = [
  { name: "2 Double Beds", size: "450 sq ft", price: "From C$345" },
  { name: "Standard Double Bed", size: "400 sq ft", price: "From C$345" },
  { name: "Deluxe Queen Bed", size: "400 sq ft", price: "From C$355" },
  { name: "Queen Bed Suite", size: "440 sq ft", price: "From C$380" },
];

const INCLUDED = [
  { icon: Coffee, label: "Continental breakfast buffet" },
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: ShieldCheck, label: "In-room safe" },
  { icon: Calendar, label: "Personally confirmed booking" },
];

const FAQ = [
  {
    q: "How much does a room cost at Malak BnB Resort?",
    a: "Rooms at Malak BnB Resort start from C$345 a night for the 2 Double Beds and Standard Double Bed rooms, from C$355 for the Deluxe Queen Bed, and from C$380 for the Queen Bed Suite. Exact rates are confirmed at the time of your reservation request.",
  },
  {
    q: "Do room rates change by season at Malak BnB Resort?",
    a: "Yes. Like most seasonal lodging, rates at Malak BnB Resort vary by time of year, with summer typically busier than the off season. Current pricing for your dates is shown when you submit a reservation request.",
  },
  {
    q: "Is a deposit required when booking a room?",
    a: "A $50.00 deposit is required for any incidentals. Full payment and deposit details are shared directly once your reservation request is confirmed, with no third-party booking fees added on top.",
  },
  {
    q: "What is included in the room rate at Malak BnB Resort?",
    a: "Every room rate at Malak BnB Resort includes continental breakfast, access to the outdoor heated pool, free Wi-Fi, air conditioning, a flat screen TV, and an in-room safe.",
  },
];

function SeasonalRatesPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[45vh] min-h-[300px] overflow-hidden">
        <img
          src="/images/room-comfort.webp"
          alt="Comfortable room at Malak BnB Resort"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Malak BnB Resort</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight">
              Seasonal Room Rates
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-muted-foreground leading-relaxed">
            Straightforward, from-pricing for every room at Malak BnB Resort in Crystal Beach, Ontario. Rates vary
            by season, and the exact price for your dates is always confirmed before you pay anything.
          </p>
        </div>
      </section>

      {/* Rate table */}
      <section className="pb-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
            {ROOMS.map((room, i) => (
              <div
                key={room.name}
                className={`flex items-center justify-between p-6 ${i !== ROOMS.length - 1 ? "border-b border-border/50" : ""}`}
              >
                <div>
                  <h3 className="font-heading text-lg text-foreground">{room.name}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{room.size}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-heading text-foreground">{room.price}</p>
                  <p className="text-xs text-muted-foreground">per night</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Rates vary by season. Submit a reservation request to see current pricing for your dates.
          </p>
          <div className="text-center mt-6">
            <Link to="/rooms" className="text-primary font-semibold text-sm hover:underline">
              See full room details and photos →
            </Link>
          </div>
        </div>
      </section>

      {/* How seasonal pricing works */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3 text-center">Rates Explained</p>
          <h2 className="text-3xl font-heading text-foreground mb-6 text-center">How Seasonal Pricing Works</h2>
          <p className="text-muted-foreground leading-relaxed text-center">
            Like most lakeside properties, rates at Malak BnB Resort shift with demand across the year, with peak
            summer weekends priced differently than a quiet weekday in the off season. There is no dynamic pricing
            trick here. The team confirms the exact rate for your dates as part of reviewing your reservation
            request, and you see it before agreeing to anything.
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl font-heading text-foreground mb-10">Every Rate Includes</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {INCLUDED.map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-card border border-border/50">
                <item.icon className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium text-foreground text-center">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Rates and Payment</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            See current rates for your dates
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and pricing before sending payment details.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request to Book
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
